/**
 * Vertex AI Proxy Client
 * Communicates with local proxy server that handles service account authentication
 * This replaces direct REST API calls which don't support RAG from browsers
 */

import type { RagResponse } from '@/types/chat';

/**
 * Vertex AI Proxy Client
 * Uses local proxy server on port 3010 for authenticated requests
 */
export class VertexAIProxyClient {
  private proxyUrl: string;

  constructor() {
    this.proxyUrl = import.meta.env.VITE_VERTEX_AI_PROXY_URL || 'http://localhost:3010';
    console.log(`[Vertex AI Proxy] Using proxy at ${this.proxyUrl}`);
  }

  /**
   * Query Vertex AI through proxy (non-streaming)
   */
  async query(prompt: string): Promise<RagResponse> {
    const startTime = Date.now();

    try {
      const response = await fetch(`${this.proxyUrl}/api/vertex-ai/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Query failed');
      }

      const data: RagResponse = await response.json();

      const elapsedTime = Date.now() - startTime;
      console.log(`[Vertex AI Proxy] Query completed in ${elapsedTime}ms`);

      return data;
    } catch (error: unknown) {
      console.error('[Vertex AI Proxy] Query error:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Proxy query failed: ${message}`);
    }
  }

  /**
   * Query with streaming response (Server-Sent Events)
   */
  async *queryStream(prompt: string): AsyncGenerator<string> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/vertex-ai/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Stream setup failed');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Split by newlines (Server-Sent Events format)
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6).trim();

            // Check for completion signal
            if (data === '[DONE]') {
              return;
            }

            try {
              const chunk = JSON.parse(data);
              if (chunk.error) {
                throw new Error(chunk.error);
              }
              if (chunk.text) {
                yield chunk.text;
              }
            } catch (parseError) {
              console.warn('[Vertex AI Proxy] Failed to parse chunk:', line);
            }
          }
        }
      }
    } catch (error: unknown) {
      console.error('[Vertex AI Proxy] Streaming error:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Proxy streaming failed: ${message}`);
    }
  }

  /**
   * Get proxy server configuration
   */
  async getConfig(): Promise<any> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/vertex-ai/config`);
      if (!response.ok) {
        throw new Error('Failed to fetch config');
      }
      return await response.json();
    } catch (error) {
      console.error('[Vertex AI Proxy] Config fetch error:', error);
      throw error;
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.proxyUrl}/health`);
      return response.ok;
    } catch (error) {
      console.error('[Vertex AI Proxy] Health check failed:', error);
      return false;
    }
  }
}

/**
 * Singleton instance
 */
export const vertexAIProxyClient = new VertexAIProxyClient();
